/**
 * Extract thumbnail frames from a video file for timeline strip display.
 * Uses offscreen <video> + Canvas to capture frames at regular intervals.
 */

const THUMBNAIL_WIDTH = 120;
const FRAMES_PER_CLIP = 15;

export interface ThumbnailFrame {
  time: number;
  dataUrl: string;
}

/**
 * Extract evenly-spaced thumbnails from a video file.
 * Returns an array of { time, dataUrl } objects.
 * Pass an AbortSignal to cancel extraction mid-loop.
 */
export async function extractThumbnails(
  videoSrc: string,
  duration: number,
  count: number = FRAMES_PER_CLIP,
  signal?: AbortSignal,
): Promise<ThumbnailFrame[]> {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.preload = "auto";
  video.crossOrigin = "anonymous";

  const cleanup = () => {
    video.src = "";
    video.load();
  };

  try {
    await new Promise<void>((resolve, reject) => {
      if (signal?.aborted) { reject(new Error("Aborted")); return; }
      video.onloadedmetadata = () => resolve();
      video.onerror = () => reject(new Error("Failed to load video for thumbnails"));
      video.src = videoSrc;
    });

    const aspectRatio = video.videoHeight / video.videoWidth;
    const thumbHeight = Math.round(THUMBNAIL_WIDTH * aspectRatio);

    const canvas = document.createElement("canvas");
    canvas.width = THUMBNAIL_WIDTH;
    canvas.height = thumbHeight;
    const ctx = canvas.getContext("2d")!;

    const frames: ThumbnailFrame[] = [];
    const interval = duration / count;

    for (let i = 0; i < count; i++) {
      if (signal?.aborted) break;

      const time = i * interval;
      try {
        await seekTo(video, time);
        if (signal?.aborted) break;
        ctx.drawImage(video, 0, 0, THUMBNAIL_WIDTH, thumbHeight);
        frames.push({
          time,
          dataUrl: canvas.toDataURL("image/jpeg", 0.6),
        });
      } catch {
        // Skip frames that fail to seek
      }
    }

    return frames;
  } finally {
    cleanup();
  }
}

function seekTo(video: HTMLVideoElement, time: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onSeeked = () => {
      clearTimeout(timeout);
      video.removeEventListener("seeked", onSeeked);
      resolve();
    };

    const timeout = setTimeout(() => {
      video.removeEventListener("seeked", onSeeked);
      reject(new Error("Seek timeout"));
    }, 5000);

    video.addEventListener("seeked", onSeeked);

    if (video.fastSeek) {
      video.fastSeek(time);
    } else {
      video.currentTime = time;
    }
  });
}
